import {
	CSSProperties,
	ComponentProps,
	PropsWithChildren,
	ReactElement,
	ReactNode,
	RefObject,
	useEffect,
	useRef,
	useState,
} from 'react';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';
import { rgba } from 'polished';
import { useWindowSize } from 'usehooks-ts';

import { Button } from './Button';
import { Portal } from './Portal';
import { Paragraph } from './Text';
import { Callout } from './Callout';

const ZIndexFix = styled(Box)<{ visible?: boolean }>`
	position: fixed;
	z-index: 3;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: ${(props) => (props.visible ? undefined : 'none')};
`;

const Backdrop = styled(Box)<{ translucent?: boolean }>`
	position: fixed;
	z-index: 1;
	top: 0px;
	left: 0px;
	width: 100%;
	height: 100%;

	opacity: ${(props) => (props.translucent ? 0 : 0.5)};
	background: ${(props) => props.theme.colors.lightBlue};
`;

const PopoverContainer = styled(Box)<{ animated?: boolean }>`
	position: fixed;
	display: flex;
	flex-direction: column;
	z-index: 1;
	background: white;
	border-radius: 6px;
	box-shadow:
		0px 2px 4px ${(props) => rgba(props.theme.colors.slateBlue, 0.5)},
		0px 8px 16px ${(props) => rgba(props.theme.colors.slateBlue, 0.25)},
		0px 32px 64px ${(props) => rgba(props.theme.colors.slateBlue, 0.5)};
	overflow: hidden;
	max-width: calc(100vw - 40px);
	max-height: min(calc(100vh - 40px), 720px);

	${(props) =>
		props.animated &&
		`transition: 0.4s cubic-bezier(0, 1, 0.25, 1) transform, 0.4s cubic-bezier(0, 1, 0, 1) opacity;`}
`;

function getBoundingBox(element?: HTMLElement): {
	left: number;
	top: number;
	width: number;
	height: number;
} {
	if (!element) {
		return { left: 0, top: 0, width: 0, height: 0 };
	}

	const parent = getBoundingBox(element.offsetParent as HTMLElement);
	const matrix = new DOMMatrix(getComputedStyle(element).transform);
	const left = element.offsetLeft + parent.left + matrix.m41;
	const top = element.offsetTop + parent.top + matrix.m42;

	return { left, top, width: element.offsetWidth, height: element.offsetHeight };
}

function getScrollOffset(element?: HTMLElement): { left: number; top: number } {
	if (!element) {
		return { left: 0, top: 0 };
	}

	const style = getComputedStyle(element);

	if (style.position === 'fixed') {
		return { left: 0, top: 0 };
	}

	if (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth) {
		const left = element.scrollLeft;
		const top = element.scrollTop;

		const parent = getScrollOffset(element.parentElement as HTMLElement);

		return { left: parent.left + left, top: parent.top + top };
	}

	return getScrollOffset(element.parentElement as HTMLElement);
}

function genie(
	origin: {
		top: number;
		left: number;
		width: number;
		height: number;
	},
	destination: {
		top: number;
		left: number;
		width: number;
		height: number;
	},
) {
	const scale = {
		x: origin.width / destination.width,
		y: origin.height / destination.height,
	};

	const translate = {
		x: origin.left + origin.width / 2 - destination.left - destination.width / 2,
		y: origin.top + origin.height / 2 - destination.top - destination.height / 2,
	};

	return ` translate(${translate.x}px, ${translate.y}px) scale(${scale.x}, ${scale.y})`;
}

export function Popover(
	props: PropsWithChildren<{
		onDismiss?: () => void;
		visible?: boolean;
		backdrop?: boolean;
		center?: boolean;
		zoom?: boolean;
		origin?: {
			top: number;
			left: number;
			width: number;
			height: number;
		};
	}>,
) {
	const ref = useRef(null);

	const [mounted, setMounted] = useState(false);

	const [size, setSize] = useState<{ width: number; height: number } | undefined>(undefined);

	const window = useWindowSize();

	const origin = props.origin ?? {
		left: window.width / 2,
		top: window.height / 2,
		width: 0,
		height: 0,
	};

	const bounds = {
		top: origin.top + origin.height + 5,
		left: origin.left,
	};

	let center = props.center;

	if (!center) {
		const fitsBelow = size && bounds.top + size.height <= window.height;
		const fitsAbove = size && origin.top - size.height - 5 >= 20;

		if (!fitsBelow && fitsAbove) {
			bounds.top = origin.top - size.height - 5;
		} else if (!fitsBelow) {
			center = true;
		}

		const fitsRight = size && bounds.left + size.width <= window.width;
		const fitsLeft = size && origin.left + origin.width - size.width >= 20;

		if (!fitsRight && fitsLeft) {
			bounds.left = origin.left + origin.width - size.width;
		} else if (!fitsRight) {
			center = true;
		}
	}

	if (center) {
		bounds.top = (window.height - (size?.height ?? 0)) / 2;
		bounds.left = (window.width - (size?.width ?? 0)) / 2;
	}

	bounds.top = Math.max(bounds.top, 20);
	bounds.left = Math.max(bounds.left, 20);

	const isCurrentRef = !!ref.current;

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const observer = new ResizeObserver((entries) => {
			const size = entries[0].contentRect;
			setTimeout(() => setSize(size));
		});

		observer.observe(ref.current);

		return () => observer.disconnect();
	}, [isCurrentRef]);

	const isCurrentSize = Boolean(size);

	useEffect(() => {
		if (size) {
			setMounted(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCurrentSize]);

	const animation: CSSProperties =
		mounted && props.visible
			? {}
			: props.zoom
			? {
					transform: 'scale(1.2, 1.2)',
					opacity: 0,
					pointerEvents: 'none',
			  }
			: {
					transform: size
						? genie(origin, {
								...bounds,
								width: size.width,
								height: size.height,
						  })
						: undefined,
					opacity: 0,
					pointerEvents: 'none',
			  };

	return (
		<ZIndexFix visible={props.visible}>
			{props.visible && (
				<Backdrop
					translucent={!(props.center || props.backdrop) || !mounted || !props.visible}
					onClick={() => props.onDismiss?.()}
				/>
			)}
			<PopoverContainer style={{ ...bounds, ...animation }} animated={mounted}>
				<div
					ref={ref}
					style={{
						display: 'flex',
						flexGrow: 1,
						flexShrink: 1,
						overflow: 'hidden',
					}}
				>
					{props.children}
				</div>
			</PopoverContainer>
		</ZIndexFix>
	);
}

export function GeometryObserver(props: {
	innerRef: RefObject<HTMLElement>;
	scroll?: boolean;
	children: (bounds: { top: number; left: number; width: number; height: number }) => ReactElement;
}) {
	const [bounds, setBounds] = useState<
		{ top: number; left: number; width: number; height: number } | undefined
	>(undefined);

	// ResizeObserver doesn't monitor the window size, so we use this hook to
	// trigger a re-render every time it changes.
	useWindowSize();

	const isCurrentInnerRef = Boolean(props.innerRef.current);

	useEffect(() => {
		if (!props.innerRef.current) {
			return;
		}

		const observer = new ResizeObserver(() => {
			const box = getBoundingBox(props.innerRef.current ?? undefined);
			const offset = getScrollOffset(props.innerRef.current ?? undefined);
			const top = box.top - (props.scroll === false ? 0 : offset.top);
			const left = box.left - (props.scroll === false ? 0 : offset.left);

			setTimeout(() => setBounds({ ...box, top, left }));
		});

		observer.observe(props.innerRef.current);

		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCurrentInnerRef]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (!props.innerRef.current) {
			return;
		}

		const box = getBoundingBox(props.innerRef.current ?? undefined);
		const offset = getScrollOffset(props.innerRef.current ?? undefined);

		const top = box.top - (props.scroll === false ? 0 : offset.top);
		const left = box.left - (props.scroll === false ? 0 : offset.left);

		if (bounds?.top === top && bounds?.left === left) {
			return;
		}

		setBounds({
			...box,
			top,
			left,
		});
	});

	if (bounds) {
		return props.children(bounds);
	} else {
		return null;
	}
}

function PopoverTarget(
	props: BoxProps &
		PropsWithChildren<{
			backdrop?: boolean;
			center?: boolean;
			onDismiss?: () => void;
			visible: boolean;
			contents: ReactNode;
		}>,
) {
	const ref = useRef(null);

	const [visible, setVisible] = useState(props.visible);
	const timer = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (timer.current) {
			clearTimeout(timer.current);
		}

		if (props.visible) {
			setVisible(true);
		} else {
			timer.current = setTimeout(() => setVisible(false), 1000);
		}
	}, [props.visible]);

	return (
		<>
			<Box ref={ref}>{props.children}</Box>
			{visible && (
				<Portal>
					<GeometryObserver innerRef={ref}>
						{(bounds) => (
							<Popover
								onDismiss={props.onDismiss}
								origin={bounds}
								visible={props.visible}
								backdrop={props.backdrop}
								center={props.center}
							>
								{props.contents}
							</Popover>
						)}
					</GeometryObserver>
				</Portal>
			)}
		</>
	);
}

Popover.Target = PopoverTarget;

function PopoverConfirm(
	props: PropsWithChildren<{
		visible: boolean;
		title: string;
		cancelTitle: string;
		confirmTitle: string;
		extraInfo?: string;
		onCancel: () => void;
		onConfirm: () => void;
		confirmType?: ComponentProps<typeof Button>['type'];
	}>,
) {
	return (
		<Popover.Target
			visible={props.visible}
			contents={
				<Box p={3}>
					<Paragraph mb={2} maxWidth={300}>
						{props.title}
					</Paragraph>
					{props.extraInfo && (
						<Callout color="blue" mb={3} mt={3} maxWidth={300}>
							{props.extraInfo}
						</Callout>
					)}

					<Flex>
						<Button type="secondary" small title={props.cancelTitle} onClick={props.onCancel} />

						<Box ml={2}>
							<Button
								small
								title={props.confirmTitle}
								onClick={props.onConfirm}
								type={props.confirmType}
							/>
						</Box>
					</Flex>
				</Box>
			}
			onDismiss={props.onCancel}
		>
			{props.children}
		</Popover.Target>
	);
}

Popover.Confirm = PopoverConfirm;
